import { SPDS_DEPRECATION_UNTIL_VERSION } from '../constants/deprecations';
import { assert, deprecate } from '@ember/debug';
import { isActive, parentExists, parseArgumentName } from '../component-validations/helpers';

const warnedByInstance = new WeakMap();

const createDeprecation = function ({ spec, argName, component }) {
  const message = spec.message ?? defaultDeprecationMessage(argName, component);

  deprecate(message, false, {
    id: spec.id,
    for: '@simplepractice/spds',
    since: spec.since,
    until: spec.until,
    ...(spec.url ? { url: spec.url } : {}),
  });
};

/** @returns {{ spec: object, argName: string }|{}} */
const pendingDeprecationForArg = function ({ args, argName, argMeta, component, warned }) {
  const rawDeprecated = argMeta?.deprecated;
  const pathOk = Boolean(rawDeprecated && parentExists(args, argName));
  const value = pathOk ? parseArgumentName(args, argName) : undefined;
  const valueOk = pathOk && isActive(value) && !warned.has(argName);
  const spec = valueOk ? resolveDeprecatedSpec(rawDeprecated, component, argName) : null;

  return spec ? { spec, argName } : {};
};

/**
 * Builds the default deprecation id for a metadata argument when `deprecated: true`.
 *
 * @param {string|null|undefined} component - e.g. `Spds::Feedback::Toast::Container`
 * @param {string} argName - Flattened arg key (may contain dots)
 * @returns {string}
 */
export function defaultDeprecationArgumentId(component, argName) {
  const path = (component ?? 'Unknown')
    .split('::')
    .map(part => part.replace(/([a-z0-9])([A-Z])/g, '$1-$2').toLowerCase())
    .join('.');
  const argSlug = argName.replace(/\./g, '-');
  return `spds.argument.${path}.${argSlug}`;
}

/**
 * @param {boolean|object} raw
 * @param {string|null|undefined} component
 * @param {string} argName
 * @returns {{ id: string, message?: string, until: string, since: object, url?: string }|null}
 */
function resolveDeprecatedSpec(raw, component, argName) {
  if (raw === true) {
    return {
      id: defaultDeprecationArgumentId(component, argName),
      until: SPDS_DEPRECATION_UNTIL_VERSION,
      since: { available: '0.0.0', enabled: '0.0.0' },
    };
  }

  if (raw && typeof raw === 'object') {
    assert(
      `deprecated argument metadata for \`${argName}\` on ${component ?? 'component'} must include a stable \`id\` string`,
      typeof raw.id === 'string' && raw.id.length > 0
    );

    return {
      id: raw.id,
      message: raw.message,
      until: raw.until ?? SPDS_DEPRECATION_UNTIL_VERSION,
      since: raw.since ?? { available: '0.0.0', enabled: '0.0.0' },
      url: raw.url,
    };
  }

  return null;
}

function defaultDeprecationMessage(argName, component) {
  return `The \`@${argName}\` argument on ${component} is deprecated.`;
}

/**
 * Emits Ember deprecations for active values on arguments marked `deprecated` in component metadata.
 *
 * Only runs when invoked from the SPDS base `validateArguments` modifier (same coverage as
 * debug argument validation): components must include `{{this.validateArguments}}` in their
 * template for this to run.
 *
 * @param {object} options
 * @param {object} options.args - Component `this.args`
 * @param {import('./builder/metadata').Metadata|{ meta?: object, component?: string }} options.metadata - Component static metadata
 * @param {object} options.componentInstance - Component instance (used for dedupe key)
 */
export function warnDeprecatedArguments({ args, metadata, componentInstance }) {
  const { component, meta } = metadata;

  if (!(meta && componentInstance)) return;

  let warned = warnedByInstance.get(componentInstance);

  if (!warned) {
    warned = new Set();
    warnedByInstance.set(componentInstance, warned);
  }

  for (const [argName, argMeta] of Object.entries(meta)) {
    const { argName: deprecatedArgName, spec } = pendingDeprecationForArg({
      args,
      argName,
      argMeta,
      component,
      warned,
    });
    if (!deprecatedArgName) continue;

    warned.add(deprecatedArgName);
    createDeprecation({ spec, argName: deprecatedArgName, component });
  }
}

export default {
  defaultDeprecationArgumentId,
  warnDeprecatedArguments,
};
